import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { path, isEmpty, propEq } from 'ramda'
import { useQuery, useMutation } from 'react-apollo'

import EvIframe from '../../../../containers/shared/evergent-iframe'
import PlanDetails from '../../../../components/plan-details'
import { useStyles } from '../../../../components/voucher-messages/voucher-messages.styles'
import ACCOUNT_CREDIT_CARDS from '../../../../../graphql/queries/account-credit-cards.gql'
import ACCOUNT_QUERY_WITH_DIR from '../../../../../graphql/queries/account-with-directives.gql'
import SUBSCRIBE_MUTATION from '../../../../../graphql/mutations/subscribe.gql'
import { ADD_VOUCHER_SUCCESSFUL_MESSAGES } from '../../../../constants'
import useCreditCardPasswordSubmit from '../../hooks/use-credit-card-picker-submit'
import RadioDotChecked from '../../../../components/icons/radio-dot-checked'
import RadioDot from '../../../../components/icons/radio-dot'
import { LoadingComponent } from '../../../../components/loading'
import CreditCardPicker from '../../../shared/credit-card-picker/credit-card-picker'
import { usePlanChangeFlag } from '../../../../hooks/usePlanChange'
import { usePriceChangeAlertFlag } from '../../../../hooks/usePriceChangeAlert'
import { usePlanDetails } from '../../../shared/hooks/use-plan-details'
import PaymentPriceChangeAlert from '../../../shared/payment-price-change-alert/payment-price-change-alert'
import { paymentAlertMessage, creditCardStatus } from '../../../shared/subscription-constants'
import { segmentTrackInitiatePurchase } from '../../../../segment/segment-track'
import { LoadingModal } from '../../../../components/loading/modal'
import SUBSCRIPTION_PAYMENT_VIEW_QUERY from '../../../../../graphql/queries/subscription-payment-view.gql'
import styles from './payment.css'

/**
 * Payment
 *
 * - fetches payment view data (iframe, pricing details, orderId)
 * - supports existing-card flow with password validation
 * - supports new-card flow through Evergent iframe
 * - submits subscribe mutation and routes to next step on success
 * - handles query/mutation failures
 */
function Payment({
  subscriptionId,
  voucher,
  continueNextStep,
  onError
}) {
  const [savedOrderId, setSavedOrderId] = useState('')
  const [selectedCardPaymentId, setSelectedCardPaymentId] = useState(null)
  const [isSelectedCardPicker, setIsSelectedCardPicker] = useState(true)
  const [validCreditCards, setValidCreditCards] = useState([])
  const [subscriptionLoading, setSubscriptionLoading] = useState(false)

  const voucherStyles = useStyles()
  const getPlanDetails = usePlanChangeFlag()
  const showPriceChangeAlert = usePriceChangeAlertFlag()
  const { getPlanDetailById } = usePlanDetails()
  const selectedPlan = getPlanDetailById(subscriptionId) || { staticDetail: { name: '', value: '' } }
  const {
    data: getPaymentDetailsData,
    loading: getPaymentDetailsLoading,
    error: getPaymentDetailsError
  } = useQuery(SUBSCRIPTION_PAYMENT_VIEW_QUERY, {
    variables: {
      voucher,
      subscriptionId,
      promotions: true
    },
    fetchPolicy: 'network-only'
  })

  const {
    iframe,
    details = [],
    orderId: paymentViewOrderId
  } = getPaymentDetailsData?.subscriptionPaymentView || {}

  // Handle payment view error
  useEffect(() => {
    if (getPaymentDetailsError || (getPaymentDetailsData && iframe === null)) {
      // Handle iframe error
      onError(getPaymentDetailsError)
    }
  }, [getPaymentDetailsData, getPaymentDetailsError, iframe, onError])

  // Account credit cards query
  const {
    data: accountCreditCardsData,
    loading: accountCreditCardsLoading,
    error: accountCreditCardsError
  } = useQuery(ACCOUNT_CREDIT_CARDS, { fetchPolicy: 'network-only' })

  // Whenever credit cards are fetched, keep only active cards.
  useEffect(() => {
    const creditcards = accountCreditCardsData?.account?.creditcards || []
    const actives = creditcards.filter(c => c.status === creditCardStatus.ACTIVE)
    setValidCreditCards(actives)
  }, [accountCreditCardsData])

  // Set default selected card when valid cards exist.
  useEffect(() => {
    if (validCreditCards.length && !selectedCardPaymentId) {
      setSelectedCardPaymentId(validCreditCards[0].id)
    }
  }, [validCreditCards, selectedCardPaymentId])

  // If a payment view explicitly returned an orderId we refetch account subscription info
  useQuery(ACCOUNT_QUERY_WITH_DIR, {
    variables: { withSubscription: true },
    fetchPolicy: 'network-only',
    skip: !paymentViewOrderId
  })

  // Subscribe mutation
  const [subscribeToPlan] = useMutation(SUBSCRIBE_MUTATION)

  // Executes subscribe mutation and routes to next step with returned order ID.
  const onCompletePayment = useCallback(async () => {
    if (!selectedCardPaymentId) {
      setSubscriptionLoading(false)
      return
    }

    try {
      const response = await subscribeToPlan({
        variables: {
          productId: subscriptionId,
          voucher,
          paymentMethodId: selectedCardPaymentId
        }
      })

      setSubscriptionLoading(false)
      if (response.errors) {
        onError(response.errors)
      } else {
        const subscribeOrderId = response.data?.subscribe
        continueNextStep(subscribeOrderId)
      }
    } catch (error) {
      setSubscriptionLoading(false)
      onError(error)
    }
  }, [
    continueNextStep,
    onError,
    selectedCardPaymentId,
    subscribeToPlan,
    subscriptionId,
    voucher
  ])

  const {
    validationLoading,
    clearInput,
    ...passwordValidation
  } = useCreditCardPasswordSubmit(
    onCompletePayment,
    setSubscriptionLoading
  )

  // Handles card switch in existing-card flow and resets password input state.
  const creditCardsPickerHandler = (paymentId) => {
    setSelectedCardPaymentId(paymentId)
    clearInput()
  }

  const DotIndicator = ({ isSelected }) => {
    const radioStyles = useStyles()
    return isSelected
      ? <RadioDotChecked className={radioStyles.icon} />
      : <RadioDot className={radioStyles.icon} />
  }

  DotIndicator.propTypes = {
    isSelected: PropTypes.bool.isRequired
  }

  // Handles iframe callback query params and tracks initiate-purchase analytics.
  const onIframeLoad = useCallback((iframeQueryParams) => {
    if (isEmpty(iframeQueryParams)) return

    if (propEq('status', 'success', iframeQueryParams)) {
      const usedOrderId = savedOrderId || path(['orderId'], iframeQueryParams)
      continueNextStep(usedOrderId)
    }

    // Add segment data analytics for initiating payment
    const sku = selectedPlan?.sku
    const paymentDue = details[0]?.value
    const payment = Number(details[1]?.value?.replace('$', ''))
    segmentTrackInitiatePurchase({
      ...selectedPlan,
      sku,
      paymentDue,
      payment,
      voucher
    })
  }, [continueNextStep, details, savedOrderId, selectedPlan, voucher])

  // Stores payment order ID and surfaces credit-card query errors.
  useEffect(() => {
    // save orderId when returned (and different)
    if (paymentViewOrderId && paymentViewOrderId !== savedOrderId) {
      setSavedOrderId(paymentViewOrderId)
      return
    }

    // If iframe is missing or credit cards query errored
    if (accountCreditCardsError) {
      onError(accountCreditCardsError)
    }
  }, [paymentViewOrderId, savedOrderId, accountCreditCardsError, onError])

  const newDetails = [
    { name: selectedPlan.staticDetail.name, value: selectedPlan.staticDetail.value },
    ...details
  ]
  const priceChangeAlert = () => {
    const isAnnuallyPlan = newDetails[0].name === getPlanDetails?.annual.staticDetail.name
    const isStandardPlan = newDetails[0].name === getPlanDetails?.standard.staticDetail.name
    return (
      <PaymentPriceChangeAlert
        isStandardPlan={isStandardPlan}
        isAnnuallyPlan={isAnnuallyPlan}
      />
    )
  }

  if (getPaymentDetailsLoading || accountCreditCardsLoading) {
    return <LoadingModal />
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <h1 className={styles.header}>Add payment details</h1>

        <p className={styles.subHeader}>
          Please provide your credit card details below to complete your account set up. You can cancel any time.
        </p>

        <p className={classNames(styles.subHeader, styles.alertMessage)}>
          {paymentAlertMessage}
        </p>

        <PlanDetails detail={newDetails} showVoucherMessage={Boolean(voucher)} />

        { showPriceChangeAlert && priceChangeAlert() }

        { voucher && (
          <>
            <div className={voucherStyles.copyMessage}>
              {ADD_VOUCHER_SUCCESSFUL_MESSAGES.successMsg}
            </div>
            <div className={voucherStyles.copy}>
              {ADD_VOUCHER_SUCCESSFUL_MESSAGES.confirmMsg}
            </div>
          </>
        )}

        {
          validCreditCards.length > 0
            ? (
              <>
                {(validationLoading || subscriptionLoading)
                  ? (
                    <div className={voucherStyles.border}>
                      <LoadingComponent pastDelay />
                    </div>
                  )
                  : (
                    <>
                      <div className={voucherStyles.border}>
                        <div
                          className={voucherStyles.selector}
                          onClick={() => setIsSelectedCardPicker(true)}
                        >
                          <DotIndicator isSelected={isSelectedCardPicker} />
                          Use an existing credit card
                        </div>
                        <div className={voucherStyles.divider} />
                        {
                          isSelectedCardPicker &&
                          (
                            <CreditCardPicker
                              validCreditCards={validCreditCards}
                              defaultCardId={selectedCardPaymentId || validCreditCards[0].id}
                              onSelected={(paymentId) => creditCardsPickerHandler(paymentId)}
                              {...passwordValidation}
                            />
                          )
                        }
                      </div>
                      <div className={classNames(voucherStyles.border, voucherStyles.paymentContainer)}>
                        <div
                          className={voucherStyles.selector}
                          onClick={() => setIsSelectedCardPicker(false)}
                        >
                          <DotIndicator
                            isSelected={!isSelectedCardPicker}
                          />
                          Use a different card
                        </div>
                        {!isSelectedCardPicker &&
                        (
                          <div className={styles.iframeWrapper}>
                            <EvIframe
                              src={iframe}
                              onLoad={onIframeLoad}
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
              </>
            )
            : (
              <div className={styles.iframeWrapper}>
                <EvIframe
                  src={iframe}
                  onLoad={onIframeLoad}
                />
              </div>
            )
        }
      </div>
    </div>
  )
}

Payment.propTypes = {
  subscriptionId: PropTypes.string.isRequired,
  voucher: PropTypes.string.isRequired,
  continueNextStep: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired
}

export default Payment
